import React, { FC, useCallback, useMemo } from 'react';
import styles from './index.module.scss';
import { Checkbox, Tooltip } from 'src/common';
import InformationFields from 'src/shared/Tender/InformationFields';
import { useOpenTenderPreview } from 'src/models/procurements/hooks';
import { useCurrentSidebar } from 'src/shared/InfoSidebar/hooks';
import classNames from 'classnames';
import { OverviewBoardActionsSingle } from '../ActionsSingle';
import TenderReportBadge from 'src/shared/Tender/TenderReportBadge';
import Badges from './Badges';
import useSelectedTenders from 'src/reactiveVars/SelectedTendersVar';
import { showTenderStatusBadge } from 'src/common/TenderStatusBadge';
import TenderRejectedBadge from 'src/shared/Tender/TenderRejectedBadge';
import { TendersBoardItem as TendersBoardViewItem } from 'src/models/procurements/TendersViews';

interface IProps {
  tender: TendersBoardViewItem;
  showCheckbox: boolean;
  onChangeSelection: (tender: TendersBoardViewItem, isSelected: boolean) => void;
}

const TendersBoardItem: FC<IProps> = props => {
  const { tender, showCheckbox, onChangeSelection } = props;
  const { id, isFilledData, isInProgress, isRead, procurementStatus, isRejected } = tender;
  const currentSidebar = useCurrentSidebar();
  const [selectedTenders] = useSelectedTenders();
  const openTenderPreview = useOpenTenderPreview();

  const isSelectedTender = useMemo(() => {
    return !!selectedTenders.find(selectedTender => selectedTender.id === id);
  }, [id, selectedTenders]);

  const openPreview = useCallback(
    e => {
      e.stopPropagation();
      openTenderPreview(id);
    },
    [id, openTenderPreview]
  );

  const onChoose = useCallback(
    e => {
      const isSelected = e.target.checked;
      onChangeSelection(tender, isSelected);
    },
    [tender, onChangeSelection]
  );

  return (
    <div
      className={classNames(styles.card, {
        [styles.isOpenInPreview]: id === currentSidebar?.id,
        [styles.isRead]: isRead,
        [styles.hasBadge]: showTenderStatusBadge(procurementStatus)
      })}
      onClick={openPreview}
    >
      <div onClick={e => e.stopPropagation()}>
        <Checkbox
          checked={isSelectedTender}
          onChange={onChoose}
          className={classNames(styles.checkbox, { [styles.isVisible]: showCheckbox })}
        />
      </div>
      <div className={styles.content}>
        <div className={styles.generalInfo}>
          <div className={styles.header}>
            <Tooltip title={tender.name}>
              <h3 className={styles.tenderName}>{tender.name}</h3>
            </Tooltip>
            {(isFilledData || isInProgress) && (
              <TenderReportBadge isFilledData={isFilledData} isInProgress={isInProgress} />
            )}
            {isRejected && <TenderRejectedBadge />}
          </div>
          <div className={styles.animationContainer}>
            <div className={classNames(styles.buyer, styles.animationMainItem)}>{tender.buyerName}</div>
            <div className={styles.animationSecItem}>
              <OverviewBoardActionsSingle tender={tender} />
            </div>
          </div>
        </div>
        <Badges tender={tender} />
      </div>
      <InformationFields
        published={tender.published}
        deadline={tender.deadline}
        estimationValue={tender.estValue}
        className={tender.keywords ? styles.informationFieldsKeywords : styles.informationFields}
        keywords={tender.keywords}
        shortMode
      />
    </div>
  );
};

export default TendersBoardItem;
